import cloneDeepLodash from "lodash.clonedeep";

/**
 * Generates unique ID to be used temporarily in the front.
 * @returns {string}
 */
export const getTempId = () => {
  return String(Math.floor(Math.random() * new Date().getTime()));
};

/**
 * Will return true if an object has no element or if all his elements are set to null, undefined or ''
 * @param {object} obj
 * @returns {boolean}
 */
export const isObjectEmpty = (obj) => {
  if (isEmpty(obj)) return true;

  return Object.values(obj).every((element) => [null, undefined, ""].includes(element));
};

/**
 * @param {object} obj
 * @param {string} key
 * @param {string} value
 * @returns {boolean}
 */
export function valueForKeyExists(obj, key, value) {
  if (!obj || typeof obj !== "object") {
    return false;
  }

  if (obj instanceof Array) {
    return obj.some((item) => valueForKeyExists(item, key, value));
  }

  return Object.values(obj).some((v) => {
    if (v === value) {
      return true;
    } else if (typeof v === "object") {
      return valueForKeyExists(v, key, value);
    }
    return false;
  });
}

/**
 * Wraps Promise.allSettled to handle rejected promises in a more convenient way.
 * Instead of having to check each individual promise, you can use this function to
 * handle all of them at once.
 *
 * @param {Promise[]} promises - An array of promises to be settled.
 * @returns {Promise} - A promise that resolves to an array of objects representing
 * the fulfillment status of each promise.
 */
export const settleAll = (promises) => {
  return Promise.allSettled(promises).then((results) => {
    const rejected = results.filter((result) => result.status === "rejected");

    if (rejected.length > 0) {
      const errors = rejected.map((result) => result.reason);
      throw new Error(`One or more promises were rejected: ${errors}`);
    }

    const fulfilledPromises = results.map(({ value }) => value);

    return fulfilledPromises;
  });
};

// =======================================
// BEGIN REPLACE LODASH
// =======================================
/**
 * "N'utilisez que les portions indispensables des librairies JavaScript et frameworks CSS"
 * @see https://github.com/cnumr/best-practices/blob/main/chapters/BP_040_fr.md
 * @see https://youmightnotneed.com/lodash
 * @see https://javascript.plainenglish.io/replacing-lodash-with-pure-javascript-functions-7c1cdbca9dd6
 * @see https://github.com/you-dont-need/You-Dont-Need-Lodash-Underscore
 * @see https://youmightnotneed.com/
 * Online Lodash Tester : @see https://codepen.io/travist/pen/jrBjBz/
 */

/**
 * get
 * Gets the value at path of object. If the resolved value is undefined, the defaultValue is returned in its place.
 * @see https://youmightnotneed.com/lodash#get
 *
 * // WARNING: This is not a drop in replacement solution and
 * // it might not work for some edge cases. Test your code!
 */
export const get = (obj, path, defValue) => {
  // If path is not defined or it has false value
  if (!path) return undefined;
  // Check if path is string or array. Regex : ensure that we do not have '.' and brackets.
  // Regex explained: https://regexr.com/58j0k
  const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g);
  // Find value
  const result = pathArray.reduce((prevObj, key) => prevObj && prevObj[key], obj);
  // If found value is undefined return default value; otherwise return the value
  return result === undefined ? defValue : result;
};

/**
 * Range
 * Creates an array of numbers (positive and/or negative) progressing from start up to, but not including, end. A step of -1 is used if a negative start is specified without an end or step. If end is not specified, it’s set to start with start then set to 0.
 * @see https://youmightnotneed.com/lodash#range
 *
 * // WARNING: This is not a drop in replacement solution and
 * // it might not work for some edge cases. Test your code!
 */
export const range = (start, end, increment) => {
  // if the end is not defined...
  const isEndDef = typeof end !== "undefined";
  // ...the first argument should be the end of the range...
  end = isEndDef ? end : start;
  // ...and 0 should be the start
  start = isEndDef ? start : 0;

  // if the increment is not defined, we could need a +1 or -1
  // depending on whether we are going up or down
  if (typeof increment === "undefined") {
    increment = Math.sign(end - start);
  }

  // calculating the lenght of the array, which has always to be positive
  const length = Math.abs((end - start) / (increment || 1));

  // In order to return the right result, we need to create a new array
  // with the calculated length and fill it with the items starting from
  // the start value + the value of increment.
  const { result } = Array.from({ length }).reduce(
    ({ result, current }) => ({
      // append the current value to the result array
      result: [...result, current],
      // adding the increment to the current item
      // to be used in the next iteration
      current: current + increment,
    }),
    { current: start, result: [] }
  );

  return result;
};

/**
 * Debounce
 * Creates a debounced function that delays invoking func until after wait milliseconds have elapsed since the last time the debounced function was invoked.
 * @see https://youmightnotneed.com/lodash#debounce
 * // WARNING: This is not a drop in replacement solution and
 * // it might not work for some edge cases. Test your code!
 */
export const debounce = (func, delay, { leading } = {}) => {
  let timerId;

  return (...args) => {
    if (!timerId && leading) {
      func(...args);
    }
    clearTimeout(timerId);

    timerId = setTimeout(() => func(...args), delay);
  };
};

/**
 * structuredClone compatibility node >= 17.0.0
 * @see https://developer.mozilla.org/en-US/docs/Web/API/structuredClone#browser_compatibility
 * @todo: disabled the 31/03/2023 because structuredClone seems to cause several trouble, retry after investigation or just use lodash
 * @param value
 * @returns {*}
 */
export const cloneDeep = (value) => {
  // if (typeof structuredClone === "function") {
  //   // eslint-disable-next-line no-undef
  //   return structuredClone(value);
  // }
  return cloneDeepLodash(value);
};

/**
 * Like lodash.upperFirst
 * upperFirst('fred');
 * // => 'Fred'
 *
 * upperFirst('FRED');
 * // => 'FRED'
 *
 * @param string
 * @returns {*}
 */
export const upperFirst = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

/**
 * Like lodash.isEmpty
 * @see https://gist.github.com/inPhoenix/45a9f9e2568126d206f1125caebcd122
 *
 * @param value
 * @returns {boolean}
 */
export const isEmpty = (value) => {
  return (
    value == null || // From standard.js: Always use === - but obj == null is allowed to check null || undefined
    (typeof value === "object" && Object.keys(value).length === 0) ||
    (typeof value === "string" && value.trim().length === 0)
  );
};

/**
 * Simple object check.
 * @param item
 * @returns {boolean}
 */
export function isObject(item) {
  return item && typeof item === "object" && !Array.isArray(item);
}

/**
 * Deep merge two objects.
 * @see https://stackoverflow.com/a/34749873/3351765
 * @param target
 * @param sources
 */
export const merge = (target, ...sources) => {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        merge(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }

  return merge(target, ...sources);
};
/**
 * @see https://dustinpfister.github.io/2019/10/03/lodash_deburr/
 */
export const deburr = (str) => {
  // pattern used to match Latin letters in lodash deburr source code
  // https://github.com/lodash/lodash/blob/master/deburr.js
  const reLatin = /[\xc0-\xd6\xd8-\xf6\xf8-\xff\u0100-\u017f]/g;
  // https://github.com/lodash/lodash/blob/master/.internal/deburrLetter.js
  /** Used to map Latin Unicode letters to basic Latin letters. */
  const deburredLetters = {
    // Latin-1 Supplement block.
    "\xc0": "A",
    "\xc1": "A",
    "\xc2": "A",
    "\xc3": "A",
    "\xc4": "A",
    "\xc5": "A",
    "\xe0": "a",
    "\xe1": "a",
    "\xe2": "a",
    "\xe3": "a",
    "\xe4": "a",
    "\xe5": "a",
    "\xc7": "C",
    "\xe7": "c",
    "\xd0": "D",
    "\xf0": "d",
    "\xc8": "E",
    "\xc9": "E",
    "\xca": "E",
    "\xcb": "E",
    "\xe8": "e",
    "\xe9": "e",
    "\xea": "e",
    "\xeb": "e",
    "\xcc": "I",
    "\xcd": "I",
    "\xce": "I",
    "\xcf": "I",
    "\xec": "i",
    "\xed": "i",
    "\xee": "i",
    "\xef": "i",
    "\xd1": "N",
    "\xf1": "n",
    "\xd2": "O",
    "\xd3": "O",
    "\xd4": "O",
    "\xd5": "O",
    "\xd6": "O",
    "\xd8": "O",
    "\xf2": "o",
    "\xf3": "o",
    "\xf4": "o",
    "\xf5": "o",
    "\xf6": "o",
    "\xf8": "o",
    "\xd9": "U",
    "\xda": "U",
    "\xdb": "U",
    "\xdc": "U",
    "\xf9": "u",
    "\xfa": "u",
    "\xfb": "u",
    "\xfc": "u",
    "\xdd": "Y",
    "\xfd": "y",
    "\xff": "y",
    "\xc6": "Ae",
    "\xe6": "ae",
    "\xde": "Th",
    "\xfe": "th",
    "\xdf": "ss",
    // Latin Extended-A block.
    "\u0100": "A",
    "\u0102": "A",
    "\u0104": "A",
    "\u0101": "a",
    "\u0103": "a",
    "\u0105": "a",
    "\u0106": "C",
    "\u0108": "C",
    "\u010a": "C",
    "\u010c": "C",
    "\u0107": "c",
    "\u0109": "c",
    "\u010b": "c",
    "\u010d": "c",
    "\u010e": "D",
    "\u0110": "D",
    "\u010f": "d",
    "\u0111": "d",
    "\u0112": "E",
    "\u0114": "E",
    "\u0116": "E",
    "\u0118": "E",
    "\u011a": "E",
    "\u0113": "e",
    "\u0115": "e",
    "\u0117": "e",
    "\u0119": "e",
    "\u011b": "e",
    "\u011c": "G",
    "\u011e": "G",
    "\u0120": "G",
    "\u0122": "G",
    "\u011d": "g",
    "\u011f": "g",
    "\u0121": "g",
    "\u0123": "g",
    "\u0124": "H",
    "\u0126": "H",
    "\u0125": "h",
    "\u0127": "h",
    "\u0128": "I",
    "\u012a": "I",
    "\u012c": "I",
    "\u012e": "I",
    "\u0130": "I",
    "\u0129": "i",
    "\u012b": "i",
    "\u012d": "i",
    "\u012f": "i",
    "\u0131": "i",
    "\u0134": "J",
    "\u0135": "j",
    "\u0136": "K",
    "\u0137": "k",
    "\u0138": "k",
    "\u0139": "L",
    "\u013b": "L",
    "\u013d": "L",
    "\u013f": "L",
    "\u0141": "L",
    "\u013a": "l",
    "\u013c": "l",
    "\u013e": "l",
    "\u0140": "l",
    "\u0142": "l",
    "\u0143": "N",
    "\u0145": "N",
    "\u0147": "N",
    "\u014a": "N",
    "\u0144": "n",
    "\u0146": "n",
    "\u0148": "n",
    "\u014b": "n",
    "\u014c": "O",
    "\u014e": "O",
    "\u0150": "O",
    "\u014d": "o",
    "\u014f": "o",
    "\u0151": "o",
    "\u0154": "R",
    "\u0156": "R",
    "\u0158": "R",
    "\u0155": "r",
    "\u0157": "r",
    "\u0159": "r",
    "\u015a": "S",
    "\u015c": "S",
    "\u015e": "S",
    "\u0160": "S",
    "\u015b": "s",
    "\u015d": "s",
    "\u015f": "s",
    "\u0161": "s",
    "\u0162": "T",
    "\u0164": "T",
    "\u0166": "T",
    "\u0163": "t",
    "\u0165": "t",
    "\u0167": "t",
    "\u0168": "U",
    "\u016a": "U",
    "\u016c": "U",
    "\u016e": "U",
    "\u0170": "U",
    "\u0172": "U",
    "\u0169": "u",
    "\u016b": "u",
    "\u016d": "u",
    "\u016f": "u",
    "\u0171": "u",
    "\u0173": "u",
    "\u0174": "W",
    "\u0175": "w",
    "\u0176": "Y",
    "\u0177": "y",
    "\u0178": "Y",
    "\u0179": "Z",
    "\u017b": "Z",
    "\u017d": "Z",
    "\u017a": "z",
    "\u017c": "z",
    "\u017e": "z",
    "\u0132": "IJ",
    "\u0133": "ij",
    "\u0152": "Oe",
    "\u0153": "oe",
    "\u0149": "'n",
    "\u017f": "s",
  };

  // https://github.com/lodash/lodash/blob/master/.internal/basePropertyOf.js
  const basePropertyOf = function (object) {
    return (key) => (object == null ? undefined : object[key]);
  };

  const deburrLetter = basePropertyOf(deburredLetters);

  return str.replace(reLatin, deburrLetter);
};

const sortByAsc = (key, cb) => {
  if (!cb) {
    cb = () => 0;
  }
  return (a, b) => (a[key] > b[key] ? 1 : b[key] > a[key] ? -1 : cb(a, b));
};

const sortByDesc = (key, cb) => {
  if (!cb) {
    cb = () => 0;
  }
  return (b, a) => (a[key] > b[key] ? 1 : b[key] > a[key] ? -1 : cb(b, a));
};
const orderByAscOrDesc = (keys, orders) => {
  let cb = () => 0;
  keys.reverse();
  orders.reverse();
  for (const [i, key] of keys.entries()) {
    if (!orders.length) {
      cb = sortByAsc(key, cb);
      continue;
    }
    const order = orders[i];
    if (order === "asc") {
      cb = sortByAsc(key, cb);
    } else if (order === "desc") {
      cb = sortByDesc(key, cb);
    } else {
      throw new Error(`Unsupported order "${order}"`);
    }
  }
  return cb;
};

/**
 * @see https://github.com/you-dont-need/You-Dont-Need-Lodash-Underscore/issues/280#issuecomment-690604745
 * @param collection
 * @param iteratees
 * @param orders
 * @returns {*|*[]}
 */
export const orderBy = (collection, iteratees, orders) => {
  if (collection == null) {
    return [];
  }
  if (!Array.isArray(iteratees)) {
    iteratees = iteratees == null ? [] : [iteratees];
  }
  if (!Array.isArray(orders)) {
    orders = orders == null ? [] : [orders];
  }
  return collection.concat().sort(orderByAscOrDesc(iteratees, orders));
};

/**
 * test with
 * words = [
 *   'StackOverflow',
 *   'camelCase',
 *   'alllowercase',
 *   'ALLCAPITALLETTERS',
 *   'CustomXMLParser',
 *   'APIFinder',
 *   'JSONResponseData',
 *   'Person20Address',
 *   'UserAPI20Endpoint',
 *   '20abcDe',
 *   '20abcDE',
 *   'A',
 *   'b',
 *   '1',
 *   null,
 *   '?',
 *   '-',
 *   '',
 *   false,
 *   undefined,
 *   0,
 *   'Foo Bar',
 *   'fooBar',
 *   '__FOO_BAR__'
 * ];
 * => console.log(words.map(kebabize));
 *
 * => result = words.map(_.kebabCase); // https://codepen.io/travist/pen/jrBjBz/
 *
 * @param str
 * @returns {*}
 */
export const kebabCase = (str) => {
  if (str === null || str === undefined || str === "") {
    return "";
  }

  return (String(str).match(/([0-9]+|([A-Z]{1}[a-z]+)|[a-z]+|([A-Z]+)(?![a-z]))/g) ?? [""])
    .map((x) => x.toLowerCase())
    .join("-");
};

/**
 * inspired by : @see https://www.measurethat.net/Benchmarks/Show/9772/0/lodash-isequal-compare-with-custom-deepequal-in-compare
 * test with :
 *
 *  object = { 'a': 1 };
 *  other = { 'a': 1 };
 * window.foo1 = { a: 1, b: 2, c: { a: 1, b: 2, c: { a: 1, b: 2 } }, d:[12] };
 * window.boo1 = { a: 1, b: 2, c: { a: 1, b: 2, c: { a: 1, b: 2 } }, d:[12] };
 * window.bar1 = { a: 1, b: 3, c: { a: 1, b: 2, c: { a: 1, b: 2 } }, d:[1] };
 *
 * // 2 levels deep
 * window.foo2 = { a: 1, b: 2, c: { a: 1, b: 2, c: { a: 1, b: 2 } } };
 * window.boo2 = { a: 1, b: 2, c: { a: 1, b: 2, c: { a: 1, b: 2 } } };
 * window.bar2 = { a: 1, b: 2, c: { a: 1, b: 3, c: { a: 1, b: 2 } } };
 *
 * // 3 levels deep
 * window.foo3 = { a: 1, b: 2, c: { a: 1, b: 2, c: { a: 1, b: 2 } } };
 * window.boo3 = { a: 1, b: 2, c: { a: 1, b: 2, c: { a: 1, b: 2 } } };
 * window.bar3 = { a: 1, b: 2, c: { a: 1, b: 2, c: { a: 1, b: 4 } } };
 *
 * console.log(object === other); // false
 * console.log(isEqual(object,other)); // true
 *
 * console.log('foo1 == boo', isEqual(window.foo1 ,window.boo1)); // true
 * console.log('foo1 == bar', isEqual(window.foo1 ,window.bar1)); // false
 *
 * console.log('foo2 == boo', isEqual(window.foo2 ,window.boo2)); // true
 * console.log('foo2 == bar', isEqual(window.foo2 ,window.bar2)); // false
 *
 * console.log('foo3 == boo', isEqual(window.foo3 ,window.boo3)); // true
 * console.log('foo3 == bar', isEqual(window.foo3 ,window.bar3)); // false
 *
 * a = ["a",1];
 * b = ["a",1];
 * c = ["c",1];
 *
 * console.log(isEqual(a,b)) // true
 * console.log(isEqual(a,c)) // false
 * console.log(a === b) // false
 * @param x
 * @param y
 * @param exceptionName
 * @returns {boolean}
 */
export const isEqual = (x, y, exceptionName) => {
  if (x === y) {
    return true; // if both x and y are null or undefined and exactly the same
  }

  if (!(x instanceof Object) || !(y instanceof Object)) {
    return false; // if they are not strictly equal, they both need to be Objects
  }
  if (x.constructor !== y.constructor) {
    // they must have the exact same prototype chain, the closest we can do is
    // test their constructor.
    return false;
  }
  for (const p in x) {
    if (!y.hasOwnProperty(p)) {
      continue; // other properties were tested using x.constructor === y.constructor
    }
    if (x[p] === y[p] || (!!exceptionName && p === exceptionName)) {
      continue; // if they have the same strict value or identity then they are equal
    }
    if (typeof x[p] !== "object") {
      return false; // Numbers, Strings, Functions, Booleans must be strictly equal
    }
    if (!isEqual(x[p], y[p])) {
      return false;
    }
  }
  for (const p in y) {
    if (y.hasOwnProperty(p) && !x.hasOwnProperty(p)) {
      return false;
    }
  }
  return true;
};

// =======================================
// END REPLACE LODASH
// =======================================
